import React from 'react';
import { motion } from 'framer-motion';
import { ShieldCheck, Mail, FileText, Lock } from 'lucide-react';
import { SectionWrapper } from '../ui/SectionWrapper';
import { CTAButton } from '../ui/CTAButton';
export function PriceBlock() {
  const fadeUp = {
    hidden: {
      opacity: 0,
      y: 30
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6
      }
    }
  };
  return <SectionWrapper id="price-block" className="bg-surface border-y border-border relative overflow-hidden" data-id="element-80">
      {/* Background grid and glow for the price section */}
      <div className="absolute inset-0 bg-grid opacity-30 pointer-events-none" data-id="element-81" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-accent/5 rounded-full blur-[100px] pointer-events-none" data-id="element-82" />

      <motion.div initial="hidden" whileInView="visible" viewport={{
      once: true,
      margin: '-100px'
    }} className="relative z-10 max-w-3xl mx-auto text-center" data-id="element-83">
        <motion.div variants={fadeUp} className="mb-16" data-id="element-84">
          <h2 className="font-heading text-lg md:text-xl font-bold text-text-primary uppercase tracking-widest mb-6 text-center" data-id="element-85">
            <span className="hidden md:inline-block w-12 h-[1px] bg-border align-middle mr-4" data-id="element-86"></span>
            La Ventaja Injusta
            <span className="hidden md:inline-block w-12 h-[1px] bg-border align-middle ml-4" data-id="element-87"></span>
          </h2>
          <p className="font-body text-lg text-text-secondary leading-relaxed" data-id="element-88">
            Mientras el resto del mundo intenta "caer bien" o "convencer", vos
            vas a estar operando a un nivel estructural. Vas a entender por qué
            ciertas personas siempre logran que se haga lo que ellos quieren y
            vas a tener el manual exacto para replicar esos mismos resultados en
            tu propia vida profesional y personal.
          </p>
        </motion.div>

        {/* Secure payment badge */}
        <motion.div variants={fadeUp} className="mb-10 flex flex-col items-center gap-4" data-id="element-89">
          <div className="flex items-center gap-3" data-id="element-90">
            <Lock className="w-5 h-5 text-accent" data-id="element-91" />
            <span className="font-heading font-bold text-text-primary uppercase tracking-widest text-sm" data-id="element-92">
              Pago 100% seguro procesado por:
            </span>
          </div>
          <div className="bg-white rounded-xl px-6 py-3 inline-block" data-id="element-93">
            <img src="https://cdn.magicpatterns.com/uploads/exAi4pqFK6hDNnRWPBHGoD/pasted-image.png" alt="Mercado Pago" className="h-10 md:h-12 w-auto" data-id="element-94" />
          </div>
        </motion.div>

        <motion.div variants={fadeUp} className="bg-bg border border-border rounded-2xl p-8 md:p-12 shadow-2xl relative" data-id="element-95">
          {/* Accent top border */}
          <div className="absolute top-0 left-0 right-0 h-1 bg-accent rounded-t-2xl glow-green" data-id="element-96" />

          <h3 className="font-heading text-xl sm:text-2xl md:text-3xl font-black text-text-primary mb-2 leading-tight" data-id="element-97">
            Dejá de adaptarte a la realidad de los demás.{' '}
            <br className="hidden md:block" data-id="element-98" />
            <span className="text-accent" data-id="element-99">Empezá a construir la tuya.</span>
          </h3>

          {/* Product stack image */}
          <div className="flex justify-center my-8" data-id="element-100">
            <img src="https://cdn.magicpatterns.com/uploads/jqZutaVs5XeKUtuHsbFkNv/heylloyd-image-1772483076580.png" alt="Arquitectura Mental de Influencia + 3 Bonus" className="w-64 md:w-80 h-auto rounded-lg shadow-[0_0_40px_rgba(0,255,136,0.15)]" data-id="element-101" />
          </div>

          <div className="my-10 py-8 border-y border-border/50" data-id="element-102">
            <span className="inline-block px-4 py-1 rounded-full bg-accent/10 border border-accent/20 text-accent font-heading font-bold text-xs tracking-widest uppercase mb-6" data-id="element-103">
              Oferta de Lanzamiento
            </span>
            <div className="flex justify-center items-start gap-2 mb-2" data-id="element-104">
              <span className="font-heading font-bold text-lg sm:text-2xl text-text-secondary mt-1 sm:mt-2" data-id="element-105">
                ARS
              </span>
              <span className="font-heading font-black text-5xl sm:text-6xl md:text-7xl text-text-primary tracking-tighter text-glow" data-id="element-106">
                19.990
              </span>
            </div>
            <p className="font-body text-text-secondary font-medium" data-id="element-107">
              Libro principal + 3 Bonus exclusivos
            </p>
          </div>

          <div className="flex flex-col items-center gap-6" data-id="element-108">
            <CTAButton href="https://checkout9.rosetadigital.com/cart/48818321064188:1" className="w-full md:w-auto md:px-16 md:py-5 text-lg" data-id="element-109">
              Quiero el acceso
            </CTAButton>

            {/* Digital product + email delivery info */}
            <div className="flex flex-col items-center justify-center gap-3 text-sm text-text-secondary" data-id="element-110">
              <div className="flex items-center gap-2" data-id="element-111">
                <FileText className="w-4 h-4 text-accent shrink-0" data-id="element-112" />
                <span data-id="element-113">Contenido digital (PDF)</span>
              </div>
              <div className="flex items-center gap-2" data-id="element-114">
                <Mail className="w-4 h-4 text-accent shrink-0" data-id="element-115" />
                <span data-id="element-116">Llega a tu email tras la compra</span>
              </div>
              <div className="flex items-center gap-2" data-id="element-117">
                <ShieldCheck className="w-4 h-4 text-accent shrink-0" data-id="element-118" />
                <span data-id="element-119">Pago único</span>
              </div>
            </div>

            {/* 7-day guarantee */}
            <div className="mt-4 pt-6 border-t border-border/30 w-full max-w-md" data-id="element-120">
              <div className="flex items-center justify-center gap-3" data-id="element-121">
                <ShieldCheck className="w-5 h-5 text-accent shrink-0" data-id="element-122" />
                <p className="font-body text-sm text-text-secondary text-center" data-id="element-123">
                  <span className="font-semibold text-text-primary" data-id="element-124">
                    Garantía de 7 días
                  </span>{' '}
                  — Si tenés algún inconveniente, te devolvemos el 100% de tu
                  dinero.
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </SectionWrapper>;
}